import { useQuery } from '@tanstack/react-query';
import {
  getDashboardStats,
  getRecentEvents,
  getSyncHistory,
  getIntegrationHealth,
  getRecordCounts,
  getMrrOverview,
  getMrrMonthDetail,
  getCsmOverview,
  getCsmCompanyUsers,
  getCsmSnapshotHistory,
  getCsmRoster,
  getCsmReconciliation,
  getCsmLeaderboards,
  getCsmPortfolioTrends,
  getCsmDeclining,
  getCsmAosOverview,
  getCsmAosCompanyDetail,
  type CsmStatusFilter,
} from '../api/dashboard';

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: getDashboardStats,
    refetchInterval: 60000,
  });
}

export function useRecentEvents(limit?: number) {
  return useQuery({
    queryKey: ['dashboard', 'events', limit],
    queryFn: () => getRecentEvents(limit),
    refetchInterval: 30000,
  });
}

export function useSyncHistory(days?: number) {
  return useQuery({
    queryKey: ['dashboard', 'syncHistory', days],
    queryFn: () => getSyncHistory(days),
    refetchInterval: 60000,
  });
}

export function useIntegrationHealth() {
  return useQuery({
    queryKey: ['dashboard', 'integrationHealth'],
    queryFn: getIntegrationHealth,
    refetchInterval: 60000,
  });
}

export function useRecordCounts() {
  return useQuery({
    queryKey: ['dashboard', 'recordCounts'],
    queryFn: getRecordCounts,
    refetchInterval: 60000,
  });
}

export function useMrrOverview() {
  return useQuery({
    queryKey: ['dashboard', 'mrr'],
    queryFn: getMrrOverview,
    refetchInterval: 3_600_000, // every hour
  });
}

export function useMrrMonthDetail(month: string | null) {
  return useQuery({
    queryKey: ['dashboard', 'mrr', 'detail', month],
    queryFn: () => getMrrMonthDetail(month!),
    enabled: !!month,
  });
}

export function useCsmOverview(params: {
  from?: string;
  to?: string;
  status?: CsmStatusFilter;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['dashboard', 'csm', params],
    queryFn: () => getCsmOverview(params),
    refetchInterval: 60000,
    staleTime: 60000,
  });
}

export function useCsmCompanyUsers(
  companyId: string | null,
  window: { from?: string; to?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'company', companyId, window],
    queryFn: () => getCsmCompanyUsers(companyId!, window),
    enabled: enabled && !!companyId,
    staleTime: 60000,
  });
}

export function useCsmRoster(all = false) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'roster', all],
    queryFn: () => getCsmRoster(all),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCsmReconciliation() {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'reconciliation'],
    queryFn: () => getCsmReconciliation(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCsmLeaderboards(params: { from?: string; to?: string; days?: number } = {}) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'leaderboards', params],
    queryFn: () => getCsmLeaderboards(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCsmPortfolioTrends(params: { from?: string; to?: string; days?: number } = {}) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'portfolioTrends', params],
    queryFn: () => getCsmPortfolioTrends(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCsmDeclining() {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'declining'],
    queryFn: () => getCsmDeclining(),
    staleTime: 5 * 60 * 1000,
    retry: false, // stale-data errors should surface, not retry-loop
  });
}

export function useCsmSnapshotHistory(
  params: { from?: string; to?: string; companyId?: string; date?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'snapshotHistory', params],
    queryFn: () => getCsmSnapshotHistory(params),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useCsmAosOverview(params: {
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'aos', params],
    queryFn: () => getCsmAosOverview(params),
    refetchInterval: 60000,
    staleTime: 60000,
  });
}

export function useCsmAosCompanyDetail(
  companyId: string | null,
  window: { from?: string; to?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['dashboard', 'csm', 'aos', 'company', companyId, window],
    queryFn: () => getCsmAosCompanyDetail(companyId!, window),
    enabled: enabled && !!companyId,
    staleTime: 60000,
  });
}
